export function formatSearchNumber(value) {
  if (value == null) return '0';
  return Number(value || 0).toLocaleString('ru-RU');
}

function normalizedMethods(searchInfo) {
  const methods = searchInfo?.methods_used || [];
  return methods.map(method => String(method).trim().toLowerCase());
}

export function isWindowedSearch(searchInfo) {
  return searchInfo?.result_mode === 'windowed' || isRankedSearch(searchInfo) || isSubstringSearch(searchInfo);
}

export function isModeNotApplicable(searchInfo) {
  return searchInfo?.result_mode === 'not_applicable' || searchInfo?.mode_applicable === false;
}

export function hasHonestTotal(searchInfo) {
  return searchInfo?.honest_total === true;
}

export function isRankedSearch(searchInfo) {
  const methods = searchInfo?.methods_used || [];
  if (!methods.length) return false;

  const normalized = normalizedMethods(searchInfo);
  const exactOnly = normalized.every(method => method === 'exact_code' || method === 'prefix_code');
  if (exactOnly) return false;
  if (isSubstringSearch(searchInfo)) return false;

  const rankedMethods = new Set([
    'semantic',
    'trigram',
    'trademark_trigram',
    'importer_trigram',
  ]);

  return normalized.length > 1 || normalized.some(method => rankedMethods.has(method));
}

export function isSubstringSearch(searchInfo) {
  const normalized = normalizedMethods(searchInfo);
  return normalized.length === 1 && ['ilike', 'ilike_description'].includes(normalized[0]);
}

export function isCodeSearch(searchInfo) {
  const normalized = normalizedMethods(searchInfo);
  return normalized.length > 0 && normalized.every(method => method === 'exact_code' || method === 'prefix_code');
}

export function getResultCountDisplay(totalEstimate, searchInfo, currentCount = null) {
  const returned = currentCount ?? searchInfo?.returned_count ?? totalEstimate ?? 0;
  const returnedValue = formatSearchNumber(returned);
  const totalValue = formatSearchNumber(totalEstimate);
  const honestTotal = hasHonestTotal(searchInfo);

  if (isModeNotApplicable(searchInfo)) {
    return {
      prefix: 'Режим',
      value: '',
      suffix: 'не подходит для этого запроса',
      compact: 'Режим не подходит для этого запроса',
      paginationPrefix: '',
      title: 'Выбранный режим не применим к такому запросу.',
    };
  }

  if (isSubstringSearch(searchInfo)) {
    return {
      prefix: 'Показано',
      value: returnedValue,
      suffix: honestTotal ? `из ${totalValue} совпадений` : 'совпадений',
      compact: honestTotal
        ? `Показано ${returnedValue} из ${totalValue} совпадений`
        : `Показано ${returnedValue} совпадений`,
      paginationPrefix: 'из',
      title: 'Поиск по подстроке ищет точную последовательность символов в описании.',
    };
  }

  if (isCodeSearch(searchInfo)) {
    const codeKind = searchInfo?.query_type === 'code_exact' ? 'по коду' : 'по префиксу';
    return {
      prefix: 'Показано',
      value: returnedValue,
      suffix: honestTotal ? `из ${totalValue} ${codeKind}` : codeKind,
      compact: honestTotal
        ? `Показано ${returnedValue} из ${totalValue} ${codeKind}`
        : `Показано ${returnedValue} ${codeKind}`,
      paginationPrefix: 'из',
      title: 'Это быстрые примеры по коду. Полную картину по базе показывает Практика оформлений.',
    };
  }

  if (honestTotal && searchInfo?.result_mode === 'windowed') {
    return {
      prefix: 'Показано',
      value: returnedValue,
      suffix: `из ${totalValue} совпадений`,
      compact: `Показано ${returnedValue} из ${totalValue} совпадений`,
      paginationPrefix: 'из',
      title: 'Показано окно совпадений. Уточните запрос или фильтры, чтобы сузить выдачу.',
    };
  }

  if (isRankedSearch(searchInfo)) {
    return {
      prefix: 'Показано',
      value: returnedValue,
      suffix: 'лучших',
      compact: `Показано ${returnedValue} лучших`,
      paginationPrefix: 'в этой выдаче',
      title: 'Это верхняя часть ранжированной выдачи, а не полный размер базы.',
    };
  }

  return {
    prefix: 'Найдено',
    value: honestTotal ? totalValue : returnedValue,
    suffix: 'записей',
    compact: `Найдено ${honestTotal ? totalValue : returnedValue}`,
    paginationPrefix: 'из',
    title: 'Количество записей, найденных по выбранному условию поиска.',
  };
}
